import { useState, useEffect, useCallback, useRef } from 'react';
import { signalsService } from '../../../services/signalsService';

const useSignalsData = () => {
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({
    signal: '',          // '' = all
    sector: '',
    exchange: '',
    is_etf: 'false',     // default to stocks only; user can switch to ETFs or both
    index: '',           // '', 'sp500', 'nasdaq100', 'dow30'
    min_score: '',
    max_score: '',
    search: '',
    sort: '-score',
    page: 1,
    page_size: 50,
  });
  const [scanner, setScanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cache = useRef({});

  const loadMeta = useCallback(async () => {
    try {
      const res = await signalsService.getMeta();
      setMeta(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const loadScanner = useCallback(async (override = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...filters, ...override };
      // Strip empty strings so the backend uses defaults
      Object.keys(params).forEach((k) => {
        if (params[k] === '' || params[k] === null || params[k] === undefined) delete params[k];
      });
      const res = await signalsService.getScanner(params);
      setScanner(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Reload scanner whenever filters change.
  useEffect(() => {
    loadScanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const updateFilter = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }));
  }, []);

  const cachedFetch = useCallback(async (key, fetchFn) => {
    if (cache.current[key]) {
      return cache.current[key];
    }
    const result = await fetchFn();
    cache.current[key] = result;
    return result;
  }, []);

  const invalidateCache = useCallback((keyPrefix) => {
    if (!keyPrefix) {
      cache.current = {};
      return;
    }
    Object.keys(cache.current).forEach((k) => {
      if (k.startsWith(keyPrefix)) delete cache.current[k];
    });
  }, []);

  return {
    meta, scanner, filters, loading, error,
    updateFilter, loadScanner, loadMeta, cachedFetch, invalidateCache,
  };
};

export default useSignalsData;
